import { Flame, Sun, Snowflake, ThermometerSnowflake } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import type { Temperature } from '../../types';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface TemperatureStyle {
  Icon: IconComponent;
  label: string;
  iconClass: string;
  selectedBorder: string;
  selectedBg: string;
  selectedText: string;
  borderL: string;
  rowBg: string;
}

export const TEMPERATURE_STYLES: Record<Temperature, TemperatureStyle> = {
  HOT: {
    Icon: Flame,
    label: 'Hot',
    iconClass: 'text-red-500 dark:text-red-400',
    selectedBorder: 'border-red-500 dark:border-red-400',
    selectedBg: 'bg-red-50 dark:bg-red-900/20',
    selectedText: 'text-red-700 dark:text-red-300',
    borderL: 'border-l-red-500',
    rowBg: 'bg-red-50/40 dark:bg-red-900/10',
  },
  WARM: {
    Icon: Sun,
    label: 'Warm',
    iconClass: 'text-amber-500 dark:text-amber-400',
    selectedBorder: 'border-amber-500 dark:border-amber-400',
    selectedBg: 'bg-amber-50 dark:bg-amber-900/20',
    selectedText: 'text-amber-700 dark:text-amber-300',
    borderL: 'border-l-amber-500',
    rowBg: 'bg-amber-50/40 dark:bg-amber-900/10',
  },
  COLD: {
    Icon: Snowflake,
    label: 'Cold',
    iconClass: 'text-sky-500 dark:text-sky-400',
    selectedBorder: 'border-sky-500 dark:border-sky-400',
    selectedBg: 'bg-sky-50 dark:bg-sky-900/20',
    selectedText: 'text-sky-700 dark:text-sky-300',
    borderL: 'border-l-sky-500',
    rowBg: 'bg-sky-50/40 dark:bg-sky-900/10',
  },
  FREEZING: {
    Icon: ThermometerSnowflake,
    label: 'Freezing',
    iconClass: 'text-slate-500 dark:text-slate-400',
    selectedBorder: 'border-slate-500 dark:border-slate-400',
    selectedBg: 'bg-slate-100 dark:bg-slate-800/40',
    selectedText: 'text-slate-700 dark:text-slate-300',
    borderL: 'border-l-slate-400',
    rowBg: 'bg-slate-50/60 dark:bg-slate-800/20',
  },
};

export const TEMPERATURE_ORDER: Temperature[] = ['HOT', 'WARM', 'COLD', 'FREEZING'];

interface TemperatureIconProps {
  temperature: Temperature;
  className?: string;
}

/**
 * Inline temperature icon — small, color-tinted Lucide icon.
 * Use next to a name (e.g., "🔥 John Doe" → <TemperatureIcon temperature="HOT" /> John Doe).
 */
export const TemperatureIcon = ({ temperature, className = 'h-4 w-4' }: TemperatureIconProps) => {
  const style = TEMPERATURE_STYLES[temperature];
  const { Icon } = style;
  return <Icon className={`${className} ${style.iconClass}`} aria-label={style.label} />;
};
